import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { claimHandle, getProfile, saveProfile, UserProfile, checkHandleAvailability } from '../lib/profile.ts';

export default function Settings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile>({});
  const [handleInput, setHandleInput] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [handleStatus, setHandleStatus] = useState<'checking' | 'available' | 'taken' | 'invalid' | 'too-short' | 'too-long' | null>(null);
  const [checkingHandle, setCheckingHandle] = useState(false);

  const checkHandle = useCallback(async (handle: string) => {
    if (!handle.trim()) {
      setHandleStatus(null);
      return;
    }
    
    setCheckingHandle(true);
    setHandleStatus('checking');
    
    try {
      const result = await checkHandleAvailability(handle, user?.uid);
      setHandleStatus(result.available ? 'available' : result.reason as any);
    } catch (error) {
      setHandleStatus('invalid');
    } finally {
      setCheckingHandle(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const p = await getProfile(user.uid);
      if (p) setProfile(p);
      setHandleInput(p?.handle || '');
    })();
  }, [user]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkHandle(handleInput);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [handleInput, checkHandle]);

  if (!user) return <div className="p-6 text-white">Sign in to edit settings.</div>;

  return (
    <div className="p-6 text-white max-w-2xl">
      <h1 className="text-2xl font-bold mb-4">Settings</h1>
      <div className="space-y-4">
        <div>
          <label className="block text-sm opacity-80">Handle</label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input 
                value={handleInput} 
                onChange={(e)=>setHandleInput(e.target.value)} 
                className="text-black rounded px-2 py-1 w-full pr-8" 
                placeholder="your-handle"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                {checkingHandle && <span className="text-gray-400 text-xs">⋯</span>}
                {!checkingHandle && handleStatus === 'available' && <span className="text-green-500 text-xs">✓</span>}
                {!checkingHandle && handleStatus === 'taken' && <span className="text-red-500 text-xs">✗</span>}
                {!checkingHandle && (handleStatus === 'invalid' || handleStatus === 'too-short' || handleStatus === 'too-long') && <span className="text-yellow-500 text-xs">!</span>}
              </div>
            </div>
            <button
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white text-sm px-3 py-1 rounded"
              disabled={busy || handleStatus !== 'available'}
              onClick={async ()=>{
                if (!user) return;
                setBusy(true); setMsg(null);
                const res = await claimHandle(user.uid, handleInput);
                setBusy(false);
                setMsg(res.ok ? 'Handle saved' : res.reason === 'taken' ? 'Handle already taken' : 'Error saving handle');
                if (res.ok) {
                  setProfile({ ...profile, handle: handleInput });
                }
              }}
            >
              Claim / Update
            </button>
          </div>
          {handleStatus && (
            <div className="text-xs mt-1">
              {handleStatus === 'checking' && <span className="text-gray-400">Checking availability...</span>}
              {handleStatus === 'available' && <span className="text-green-400">Handle is available</span>}
              {handleStatus === 'taken' && <span className="text-red-400">Handle is already taken</span>}
              {handleStatus === 'invalid' && <span className="text-yellow-400">Invalid handle (only letters, numbers, hyphens, underscores)</span>}
              {handleStatus === 'too-short' && <span className="text-yellow-400">Handle must be at least 3 characters</span>}
              {handleStatus === 'too-long' && <span className="text-yellow-400">Handle must be 24 characters or less</span>}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm opacity-80">Display Name</label>
          <input value={profile.displayName || ''} onChange={(e)=>setProfile({ ...profile, displayName: e.target.value })} className="text-black rounded px-2 py-1 w-full" />
        </div>

        <div>
          <label className="block text-sm opacity-80">Bio</label>
          <textarea value={profile.bio || ''} onChange={(e)=>setProfile({ ...profile, bio: e.target.value })} className="text-black rounded px-2 py-1 w-full h-24" />
        </div>

        <div>
          <label className="block text-sm opacity-80 mb-1">Links</label>
          {['cardmarket','vinted','olx','shop','website','twitter','instagram'].map((k)=> (
            <div key={k} className="flex items-center gap-2 mb-2">
              <div className="w-28 text-sm opacity-80">{k}</div>
              <input value={(profile.links?.[k as keyof NonNullable<UserProfile['links']>] as string) || ''} onChange={(e)=>setProfile({ ...profile, links: { ...(profile.links||{}), [k]: e.target.value } })} className="text-black rounded px-2 py-1 flex-1" />
            </div>
          ))}
        </div>

        <div>
          <label className="block text-sm opacity-80">Visibility</label>
          <select value={profile.visibility || 'public'} onChange={(e)=>setProfile({ ...profile, visibility: e.target.value as any })} className="text-black rounded px-2 py-1">
            <option value="public">Public</option>
            <option value="private">Private</option>
            <option value="link">Link only</option>
          </select>
        </div>

        {msg && <div className="text-green-300 text-sm">{msg}</div>}

        <div className="pt-2">
          <button
            className="bg-green-600 hover:bg-green-500 text-white text-sm px-4 py-2 rounded"
            disabled={busy}
            onClick={async ()=>{
              if (!user) return;
              setBusy(true); setMsg(null);
              await saveProfile(user.uid, profile);
              setBusy(false); setMsg('Saved');
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}


